import {
  ChangeEvent,
  ComponentProps,
  DragEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import SharedDialog from '../../../shared/shared-dialog';
import SharedButton from '../../../shared/buttons/shared-button';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  styled,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material/styles';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ImageOutlined from '@mui/icons-material/ImageOutlined';

interface ProfilePicUploadDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  onSave: (file: File) => void;
  isUploading?: boolean;
  isLoading?: boolean;
  secondaryButton?: ComponentProps<typeof SharedDialog>['secondaryButton'];
  children?: ReactNode;
}

interface UploadInstructionsProps {
  sx?: SxProps<Theme>;
}

interface BrowseFileButtonProps {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
}

interface SelectedFileStateProps {
  file: File;
  previewUrl?: string;
  onRemove: () => void;
  disabled: boolean;
}

interface UploadErrorStateProps {
  error: string;
  onBrowse: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
}

interface EmptyUploadStateProps {
  onBrowse: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function UploadInstructions({ sx }: UploadInstructionsProps) {
  const theme = useTheme();

  return (
    <Stack sx={sx} alignItems={'center'}>
      <Typography
        sx={{
          fontSize: 12,
          color: theme.palette.semantic.text.support,
        }}
      >
        We support .jpg and .png files with file size up to 2MB
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          color: theme.palette.semantic.text.support,
          mt: 0.5,
        }}
      >
        Your photo may take a couple of seconds before it is uploaded.
      </Typography>
    </Stack>
  );
}

function BrowseFileButton({ onClick, disabled }: BrowseFileButtonProps) {
  return (
    <SharedButton variant="primary" onClick={onClick} disabled={disabled}>
      Browse File
    </SharedButton>
  );
}

function UploadingState() {
  const theme = useTheme();

  return (
    <Stack
      alignItems="center"
      sx={{
        width: '100%',
        px: 5,
      }}
    >
      <LinearProgress
        variant="indeterminate"
        sx={{
          width: '100%',
          height: 8,
          borderRadius: 4,
          mt: 2.5,
          backgroundColor: theme.palette.semantic.surface.bg3,
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.semantic.icon.active,
          },
        }}
      />

      <Typography
        sx={{
          fontSize: 16,
          color: theme.palette.semantic.text.title2,
          mt: 3,
        }}
      >
        Uploading photo, please wait...
      </Typography>

      <UploadInstructions
        sx={{
          mt: 5.6,
        }}
      />
    </Stack>
  );
}

function EmptyUploadState({ onBrowse, disabled }: EmptyUploadStateProps) {
  const theme = useTheme();

  return (
    <>
      <Typography
        sx={{
          fontSize: 16,
          color: theme.palette.semantic.text.title2,
          mb: 1,
        }}
      >
        Drag and drop photo
      </Typography>

      <Typography
        sx={{
          fontSize: 16,
          color: theme.palette.semantic.text.support,
          mb: 1.5,
        }}
      >
        or
      </Typography>

      <BrowseFileButton onClick={onBrowse} disabled={disabled} />

      <UploadInstructions sx={{ mt: 2.1 }} />
    </>
  );
}

function UploadErrorState({
  error,
  onBrowse,
  disabled,
}: UploadErrorStateProps) {
  const theme = useTheme();

  return (
    <>
      <Typography
        sx={{
          fontSize: 18,
          lineHeight: 1.2,
          color: theme.palette.semantic.text.error2,
          mb: 0.75,
        }}
      >
        {error}
      </Typography>

      <UploadInstructions sx={{ mb: 1.5 }} />

      <Typography
        sx={{
          fontSize: 18,
          color: theme.palette.semantic.text.title2,
          mb: 1,
        }}
      >
        Drag and drop photo
      </Typography>

      <Typography
        sx={{
          fontSize: 18,
          color: theme.palette.semantic.text.support,
          mb: 1.5,
        }}
      >
        or
      </Typography>

      <BrowseFileButton onClick={onBrowse} disabled={disabled} />
    </>
  );
}

function SelectedFileState({
  file,
  onRemove,
  disabled,
}: SelectedFileStateProps) {
  const theme = useTheme();

  return (
    <>
      <Stack alignItems="center" spacing={3} mt={2}>
        <Stack direction="row" gap={1}>
          <ImageOutlined
            sx={{
              width: 21,
              height: 21,
            }}
          />

          <Typography
            sx={{
              fontSize: 16,
              color: theme.palette.semantic.text.title2,
              maxWidth: 300,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </Typography>
        </Stack>

        <SharedButton
          size="small"
          variant="secondary"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          disabled={disabled}
        >
          Remove Photo
        </SharedButton>
      </Stack>

      <UploadInstructions sx={{ mt: 2.5 }} />
    </>
  );
}

export default function ProfilePicUploadDialog({
  open,
  handleClose,
  title,
  onSave,
  isUploading = false,
  isLoading = false,
  secondaryButton,
  children,
}: ProfilePicUploadDialogProps) {
  const theme = useTheme();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const removeSelectedPhoto = () => {
    setUploadedFile(null);
    setFileError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateAndSetFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (!ACCEPTED_FILE_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
      setUploadedFile(null);
      setFileError('Sorry this file is not supported');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFileError('');
    setUploadedFile(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    validateAndSetFile(event.target.files?.[0]);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (isLoading) return;
    event.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    validateAndSetFile(event.dataTransfer.files?.[0]);
  };

  const handleBrowseFile = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const hasSelectedFile = uploadedFile !== null;
  const hasError = Boolean(fileError);

  const renderUploadState = () => {
    if (isUploading) {
      return <UploadingState />;
    }

    if (hasSelectedFile) {
      return (
        <SelectedFileState
          file={uploadedFile}
          onRemove={removeSelectedPhoto}
          disabled={isLoading}
        />
      );
    }

    if (hasError) {
      return (
        <UploadErrorState
          error={fileError}
          onBrowse={handleBrowseFile}
          disabled={isLoading}
        />
      );
    }

    return (
      <EmptyUploadState onBrowse={handleBrowseFile} disabled={isLoading} />
    );
  };

  useEffect(() => {
    if (open) return;

    setUploadedFile(null);
    setFileError('');
    setIsDragging(false);
    dragCounter.current = 0;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const preventDefault = (e: globalThis.DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);

    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, [open]);

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={title}
      dialogContent={
        <Stack
          alignItems="center"
          sx={{
            width: '100%',
            px: 1,
            pb: 1,
          }}
        >
          <Box
            data-testid="dropzone"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              width: '100%',
              minHeight: '272px',
              border: `1px solid ${theme.palette.semantic.surface.primary2}`,
              borderRadius: '4px',
              backgroundColor: isDragging
                ? theme.palette.semantic.surface.hover
                : theme.palette.semantic.surface.primary3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 2,
              py: 2,
              '&:hover': {
                backgroundColor: isDragging
                  ? theme.palette.semantic.surface.hover
                  : theme.palette.semantic.surface.primary3,
              },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: theme.palette.semantic.surface.bg1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              {hasError ? (
                <ErrorOutline
                  sx={{
                    color: theme.palette.semantic.icon.error,
                    fontSize: 32,
                  }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{
                    color: theme.palette.semantic.icon.primary,
                    fontSize: 32,
                  }}
                />
              )}
            </Box>

            {renderUploadState()}

            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              onChange={handleFileChange}
              disabled={isLoading}
            />
          </Box>

          {children}
        </Stack>
      }
      noCancel
      buttonAlignment="center"
      primaryButton={{
        loading: isLoading,
        action: () => {
          if (uploadedFile) onSave(uploadedFile);
        },
        disabled: !hasSelectedFile || hasError || isLoading,
        title: 'Save Photo',
        sx: {
          textTransform: 'none',
          mb: 2,
        },
      }}
      secondaryButton={secondaryButton}
    />
  );
}
